import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isEmpty } from 'lodash';
import * as actions from "../../../store/actions";
import {
  Paper, Container, Table, TableBody, TableCell, TableFooter, TablePagination, TableRow, TextField, Typography, CircularProgress,
} from '@mui/material';
import { i18n } from '../../../translate/i18n';
import EnhancedTableHead from "../../../UI/MaterialTableOverrides/EnhancedTableHead";
import TableActionButtons from "../../../UI/MaterialTableOverrides/TableActionButtons";
import TableActionConfirmButtons from "../../../UI/MaterialTableOverrides/TableActionConfirmButtons";
import TablePaginationActionsWrapped from "../../../UI/MaterialTableOverrides/TablePaginationActions";
import TableTitle from '../../../UI/MaterialTableOverrides/TableTitle';
import { deleteCheckMessage } from "../../../utils/deleteCheckMessage";
import Spinner from '../../../components/Spinner/index';

export default function ServiceTypes() {
  const dispatch = useDispatch();
  let textInput = useRef(null);

  const [page, setPage] = useState(1);
  const [alreadySerched, setAlreadySerched] = useState(false);
  const [limit, setLimit] = useState(5);
  const [searchText, setSearchText] = useState("");
  const [orderBy, setOrderBy] = useState("name");
  const [order, setOrder] = useState("asc");
  const [firstLoad, setFirstLoad] = useState(true);
  const [editedIndex, setEditedIndex] = useState(-1);
  const [areaEditText, setAreaEditText] = useState("");
  const [isAddingNewArea, setIsAddingNewArea] = useState(false);
  const [actionRow, setActionRow] = useState("");

  const fetchServiceTypes = useCallback(
    () => dispatch(actions.fetchServiceTypes(page, limit, searchText, order, orderBy)),
    [page, limit, searchText, order, orderBy]
  );

  const fetchCheckDelete = useCallback(
    (index) => dispatch(actions.checkDeleteServiceTypes(index)));

  const deleteArea = (index, page, limit, searchText, order, orderBy) => dispatch(actions.deleteServiceTypes(index, page, limit, searchText, order, orderBy))
  const updateArea = (index, body, page, limit, searchText, order, orderBy) => dispatch(actions.updateServiceTypes(index, body, page, limit, searchText, order, orderBy))
  const postServiceTypes = (body, page, limit, searchText, order, orderBy) => dispatch(actions.postServiceTypes(body, page, limit, searchText, order, orderBy))

  const serviceTypes = useSelector(state => state.serviceTypes.serviceTypes);
  const serviceTypeFilter = useSelector(state => state.serviceTypes.serviceTypeFilter);
  const checkDeleteServiceTypes = useSelector(state => state.serviceTypes.checkDelete);

  const loading = useSelector(state => state.serviceTypes.loading);

  const headRows = [
    { id: "actions", numeric: false, disablePadding: false, label: i18n.t('headRowsTypeService.actions') },
    { id: "name", numeric: false, disablePadding: false, label: i18n.t('headRowsTypeService.name') }
  ];

  useEffect(() => {
    if (!isEmpty(serviceTypeFilter)) {
      setPage(serviceTypeFilter.page);
      setLimit(serviceTypeFilter.limit);
      setSearchText(serviceTypeFilter.searchText);
      setOrderBy(serviceTypeFilter.orderBy);
      setOrder(serviceTypeFilter.order);
    }
    setFirstLoad(false);
  }, []);

	useEffect(() => {
		dispatch(actions.setOpenDrawer(true))
	}, [])

  useEffect(() => {
    if (!firstLoad) {
      fetchServiceTypes(page, limit, searchText, order, orderBy);
      setAlreadySerched(true);
    }
  }, [firstLoad, page, limit, order, orderBy]);

  useEffect(() => {
    if (searchText !== "" || alreadySerched) {
      const timer = setTimeout(() => {
        if (searchText === textInput.current.value) {
          fetchServiceTypes(page, limit, searchText, order, orderBy);
        }
      }, 200);
      return () => {
        clearTimeout(timer);
      };
    }
  }, [searchText, textInput]);

  const handleChangePage = (e, page) => {
    setEditedIndex(-1);
    setPage(page + 1);
    setIsAddingNewArea(false)
  };

  const handleChangeRowsPerPage = e => {
    setLimit(e.target.value);
    setPage(1);
    setEditedIndex(-1);
    setIsAddingNewArea(false)
  };

  const handleRequestSort = (event, property) => {
    const isDesc = orderBy === property && order === "desc";
    setOrder(isDesc ? "asc" : "desc");
    setOrderBy(property);
  };

  const editComponent = (index, obj) => {
    setEditedIndex(index);
    setAreaEditText(obj.name);
    setActionRow('editing');
    setIsAddingNewArea(false)
  };

  const cancelEdit = () => {
    setEditedIndex(-1);
  };

  const deleteComponent = (index, objIndex) => {
    fetchCheckDelete(objIndex);
    setEditedIndex(index);
    setActionRow('deleting');
  };

  const updateComponent = (index) => {
    if (actionRow === 'editing') {
      if (areaEditText === '') return;
      updateArea(index, { name: areaEditText }).then((res) => {
        if (res === 'success') {
          setEditedIndex(-1);
          fetchServiceTypes();
        }
      })
    } else if (actionRow === 'deleting') {
      deleteArea(index).then((res) => {
        if (res === 'success') {
          setEditedIndex(-1);
          fetchServiceTypes();
        }
      })
    }
  };

  const addNewAreaRow = () => {
    setIsAddingNewArea(!isAddingNewArea)
    setEditedIndex(-1);
    setAreaEditText("");
  }

  const createComponent = () => {
    postServiceTypes({ name: areaEditText }).then((res) => {
      if (res === 'success') {
        setIsAddingNewArea(false)
        fetchServiceTypes();
      }
    })
  }

  const createCancel = () => {
    setIsAddingNewArea(false)
  }

  const editArea = () => {
    return (
      <TextField
        variant="standard"
        style={styles.editWidth}
        multiline
        id="area"
        value={areaEditText}
        margin="normal"
        onChange={event => {
          setAreaEditText(event.target.value.toUpperCase());
        }} />
    );
  };

  const styles = {
    table: {
      position: 'relative',
      marginBottom: 50,
    },
    opacityContainer: {
      opacity: loading ? '0.3' : '1',
    },
    spinner: {
      position: 'absolute',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      height: '100%',
      top: '-5px',
      opacity: '1',
      zIndex: 2,
    },
    noResults: {
      height: '100px',
    },
    deleteTextConfirm: {
      marginLeft: '-30px',
      fontWeight: 'bold',
    },
    editWidth: {
      width: '90%'
    }
  }

  return <>
    <Container maxWidth="sm">
      <Paper>
        <TableTitle
          title={i18n.t('tableTitleServiceType.title')}
          placeholder={i18n.t('tableTitleServiceType.placeholder')}
          inputRef={textInput}
          value={searchText}
          onChange={event => {
            setPage(1);
            setSearchText(event.target.value);
          }}
          addRow={() => addNewAreaRow()}
        />
        <Table style={styles.table}>
          <tbody>
            <tr>
              <td>
                {loading &&
                  <div style={styles.spinner}>
                    <Spinner />
                  </div>}
                {serviceTypes.rows.length === 0 && !isAddingNewArea &&
                  <div style={styles.noResults}>
                    {!loading && <Typography style={styles.spinner}>{i18n.t('tableTitleServiceType.Typography')}</Typography>}
                  </div>}
              </td>
            </tr>
          </tbody>
          <EnhancedTableHead
            style={styles.opacityContainer}
            headRows={headRows}
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
          />
          <TableBody style={styles.opacityContainer}>
            {isAddingNewArea &&
              <TableRow>
                <TableActionConfirmButtons
                  createComponent={createComponent}
                  createCancel={createCancel}
                />
                <TableCell>{editArea()}</TableCell>
              </TableRow>
            }
            {serviceTypes.rows.map((serviceType, index) => (
              <TableRow key={serviceType._id}>
                <TableActionButtons
                  objContent={serviceType}
                  deleteComponent={deleteComponent}
                  editComponent={editComponent}
                  updateComponent={updateComponent}
                  cancelEdit={cancelEdit}
                  editedIndex={editedIndex}
                  index={index}
                  action={actionRow}
                  checkDelete={isEmpty(checkDeleteServiceTypes)}
                />
                <TableCell>
                  {index === editedIndex ?
                    actionRow === 'editing' ?
                      editArea() :
                      <Typography variant='h6' style={styles.deleteTextConfirm}>
                        {isEmpty(checkDeleteServiceTypes) ? i18n.t('tableCellServiceType.Typography') : deleteCheckMessage(checkDeleteServiceTypes)}
                      </Typography> :
                    serviceType.name
                  }
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter style={styles.opacityContainer}>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                count={serviceTypes.count}
                rowsPerPage={parseInt(limit)}
                page={page - 1}
                SelectProps={{
                  native: true
                }}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                ActionsComponent={TablePaginationActionsWrapped}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </Paper>
    </Container>
  </>;

}



